<template>
    <div>
        <div id="chat-messages">
            <div v-for="(message, index) in messages" :key="index" class="message"
                :class="[message.class, { 'in-progress': message.generating }]">
                <vue-markdown :source="message.content" />
                <div v-if="!message.generating && !message.user" class="chat-message__tail chat-message__tail--bot">
                </div>
                <div v-if="!message.generating && message.user" class="chat-message__tail chat-message__tail--user">
                </div>
            </div>
        </div>
        <div v-if="chatInProgress" class="make-model-bot__chat-container">
            <input ref="chatbotTextbox" class="make-model-bot__chat-text" type="text" v-model="chatText"
                placeholder="Type a message..." :disabled="!showBotChat" @keyup.enter="SubmitMessageBox" />
            <button class="send-button make-model-bot__send-button" @click="SubmitMessageBox"
                :disabled="!showBotChat || chatText.length === 0">
                <div class="send-img">
                    <submitButton :buttonDisabled="!showBotChat || chatText.length === 0" :conversationId="conversationId" />
                </div>
            </button>
        </div>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Prop, Ref } from 'vue-property-decorator';
import IVehicle from '@/models/vehicle/interfaces/vehicle.d';
import IRange from '@/models/vehicle/interfaces/range.d';
import VueMarkdown from 'vue-markdown-render';
import { trimChatbotService } from '@/services/TrimChatbotService';
import ChatBotType from '@/enums/ChatBotType';
import VehicleTrimBotSubmitComponent from '../VehicleTrimBotSubmitComponent.vue';

@Component({
    components: {
        submitButton: VehicleTrimBotSubmitComponent,
        VueMarkdown
    }
})

export default class ChatBotAnswerSupportComponent extends Vue {
    @Prop({ default: '' })
    public readonly question!: string;

    @Prop({ default: '' })
    public readonly conversationId!: string;

    public showBotChat: boolean = false;

    public awaitingResponse: boolean = false;

    public chatText: string = '';

    public messages: any[] = [];

    public isKeyboardOpen: boolean = false;

    public chatInProgress: boolean = true;

    public showFinishChatButtons: boolean = false;

    @Ref('chatbotTextbox')
    public readonly chatbotTextbox!: HTMLTextAreaElement;

    public possibleResponses: string[] = [];

    private getCorrelationId(): string {
        return this.$store.getters['initialSettingsModule/correlationId'];
    }

    public get currentRange(): IRange {
        return this.$store.getters['vehicleModule/currentRange'];
    }

    public get currentVehicle(): IVehicle {
        return this.$store.getters['vehicleModule/currentVehicle'];
    }

    public startChat(): void {
        this.messages.push({ class: 'chat-message__message chat-message__bot-message', content: `How can I assist you with "${this.question}"`, user: false });

        this.showBotChat = true;
    }

    public resumeChat(): void {
        this.chatInProgress = true;
        this.showFinishChatButtons = false;
    }

    public finishChat(): void {
        this.chatInProgress = false;
        this.showFinishChatButtons = false;
        this.$emit('onChatFinished');
    }

    public async sendMessage(): Promise<void> {
        this.awaitingResponse = true;

        const consumerText = this.chatText.trim();
        this.chatText = '';

        this.messages.push({ class: 'chat-message__message chat-message__user-message', content: consumerText, user: true });
        this.scrollDown();

        const responseMessage = {
            class: 'chat-message__message chat-message__bot-message', content: '', generating: true, user: false
        };
        this.messages.push(responseMessage);

        await trimChatbotService.sendMessage(
            consumerText,
            parseInt(this.currentRange.rangeExternalReferenceId, 10),
            this.getCorrelationId(),
            this.conversationId,
            false,
            ChatBotType.GeneralBot,
            (token) => {
                responseMessage.content += token;
                this.scrollDown();
            }
        );

        this.awaitingResponse = false;
        responseMessage.generating = false;

        this.finishChat();

        this.scrollDown();
    }

    public SubmitMessageBox() {
        this.chatInProgress = false;
        this.sendMessage();
    }

    public scrollDown(): void {
        this.$nextTick(() => {
            const nestedElement = document.getElementById('chat-messages');

            if (nestedElement === null) {
                throw new Error();
            }
            nestedElement.scrollTo(0, nestedElement.scrollHeight);
        });
    }

    mounted() {
        this.startChat();
    }
}
</script>
